import { BaseAction } from "../../../core/actions/BaseAction";
import { getRecoilStateValueWithType, updateRecoilStateWithType } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IMultiBookingOfferResponseAtom } from "../../../Orchestrator/Recoils/MultiBookingAtom";
import dayjs from "dayjs";
import { convertOffersToTrains, processOffers } from "../Search/GetSearchResultAction";
import { ticketClassTypeEnum, TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { TrainType } from "../../../Orchestrator/Types/Types/TrainTypes";
import { IMultiBooking } from "../../../Orchestrator/Types/Index";
import { getTrainClassTypeFormProductCode } from "../../../Lib/Utils/validationUtil";

interface IPreselectMultiBookingTrainsActionParams {
  multiBookingOfferResponse: IMultiBookingOfferResponseAtom,
  formValues: IMultiBooking
}

export class PreselectMultiBookingTrainsAction extends BaseAction<void, IPreselectMultiBookingTrainsActionParams> {
  filteredAvailableDates = () => {
    const multiBookingFormValuesState = getRecoilStateValueWithType("multiBookingFormValuesAtom")
    const multiBookingTravelDatesState = getRecoilStateValueWithType("multiBookingTravelDatesAtom")

    const hasPreferredDaysSelected = Object.values(multiBookingFormValuesState?.preferredDays || {}).some(value => value);
    const startDate = dayjs(multiBookingTravelDatesState?.start);
    const endDate = dayjs(multiBookingTravelDatesState?.end);
    let dateArray: dayjs.Dayjs[] = [];

    let currentDate = startDate;
    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      dateArray.push(currentDate);
      currentDate = currentDate.add(1, 'day');
    }

    if (hasPreferredDaysSelected) {
      const preferredDays = multiBookingFormValuesState?.preferredDays;

      dateArray = dateArray.filter(date => {
        const dayOfWeek = date.format('dddd').toLowerCase();
        return preferredDays ? preferredDays[dayOfWeek as keyof typeof preferredDays] : false;
      });
    }

    return dateArray.map(date => date.format("YYYY-MM-DD"));
  }

  override async run(): Promise<void> {
    try {
      const { multiBookingOfferResponse, formValues } = this.params!;
      if (!multiBookingOfferResponse) return;

      const selectedTrains: any[] = [];

      const availableDates = this.filteredAvailableDates();

      if (multiBookingOfferResponse.outbound) {
        const outboundDatedOffers = multiBookingOfferResponse.outbound.datedOffers.length > 0 ? multiBookingOfferResponse.outbound.datedOffers : multiBookingOfferResponse.inbound?.datedOffers || [];
        availableDates.forEach(offerDate => {
          const outboundMatchingTrain = multiBookingOfferResponse.outbound?.trips?.sort((a, b) => a.startTime.localeCompare(b.startTime)).find(trip => dayjs(trip.startTime).isSame(dayjs(offerDate), 'day'));
          const outboundMatchingTrainOffers = outboundMatchingTrain ? multiBookingOfferResponse.outbound?.datedOffers.find(i => i.tripDate === dayjs(outboundMatchingTrain.startTime).format("YYYY-MM-DD")) : null;

          let outboundTrainInfo: TrainType | null = null;
          const isRoundTripBooking = true;
          if (!isRoundTripBooking && !outboundMatchingTrain) {
            return false;
          } else if (outboundMatchingTrain && outboundMatchingTrainOffers) {
            let outboundTrainOffers = processOffers([outboundMatchingTrain], outboundMatchingTrainOffers.offers);
            const [convertedOutboundTrains] = convertOffersToTrains(outboundTrainOffers, TrainTypeEnum.outbound);
            outboundTrainInfo = {
              ...convertedOutboundTrains,
              ...(convertedOutboundTrains && {
                selectedPriceClass: getTrainClassTypeFormProductCode(formValues.ticketOption || "") || ticketClassTypeEnum.standardValue
              })
            }
          }

          const inboundMatchingTrain = multiBookingOfferResponse.inbound?.trips?.sort((a, b) => a.startTime.localeCompare(b.startTime)).find(trip => dayjs(trip.startTime).isSame(dayjs(offerDate), 'day'));
          const inboundMatchingTrainOffers = inboundMatchingTrain ? multiBookingOfferResponse.inbound?.datedOffers.find(i => i.tripDate === dayjs(inboundMatchingTrain.startTime).format("YYYY-MM-DD")) : null;
          let inboundTrainInfo: TrainType | null = null;
          if (inboundMatchingTrain && inboundMatchingTrainOffers) {
            let inboundTrainOffers = processOffers([inboundMatchingTrain], inboundMatchingTrainOffers.offers);
            const [convertedInboundTrains] = convertOffersToTrains(inboundTrainOffers, TrainTypeEnum.inbound);
            inboundTrainInfo = {
              ...convertedInboundTrains,
              ...(convertedInboundTrains && {
                selectedPriceClass: getTrainClassTypeFormProductCode(formValues.ticketOption || "") || ticketClassTypeEnum.standardValue
              })
            }
          }

          selectedTrains.push({
            date: offerDate,
            outbound: {
              selected: outboundTrainInfo?.isSoldOut ? false : Boolean(outboundTrainInfo),
              train: outboundTrainInfo && outboundTrainInfo.selectedPriceClass ? outboundTrainInfo : null
            },
            inbound: {
              selected: inboundTrainInfo?.isSoldOut ? false : Boolean(inboundTrainInfo),
              train: inboundTrainInfo && inboundTrainInfo.selectedPriceClass ? inboundTrainInfo : null
            }
          })
        });
      }
      updateRecoilStateWithType("multiBookingSelectedTrainsAtom", selectedTrains.length > 0 ? selectedTrains : [])
    } catch (error) {
      console.error("log error in preselectMultiBookingTrainsAction", error)
    }
  }
}

export const preselectMultiBookingTrainsAction = new PreselectMultiBookingTrainsAction();
